import {Component, inject, Input, OnDestroy, OnInit, computed, signal} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {PatientService} from '../../patients/services/patient.service';
import {CoreService} from 'src/app/core/services/core.service';
import {MedicationItemAddCardComponent} from '../medication-item-add-card/medication-item-add-card.component';
import {MatDialog} from '@angular/material/dialog';
import {PatientEvent, PatientEventsService} from '../../patients/services/patient-events.service';
import {MedicationRepositoryService, Medication, Patient, ScriptRepositoryService, ScriptType, UserSettings, WebSocketService, Events} from '@xmedical/core';
import {MedicationDisplayItem, MedicationRemovedEvent} from '@xmedical/shared';

@Component({
    selector: 'patient-medication-widget',
    templateUrl: './medication-widget.component.html',
    styleUrls: ['./medication-widget.component.scss'],
    standalone: false
})
export class MedicationWidgetComponent implements OnInit, OnDestroy {

  @Input() title: string;
  @Input() scriptType: ScriptType;
  error = '';
  loading = false;
  settings: UserSettings;
  medication: Medication[] = [];
  private subscriptions: Subscription[] = [];
  private channelName = '';
  private readonly eventName = Events.MedicationMedicationListChanged;

  private medicationRepository = inject(MedicationRepositoryService);
  private matDialog = inject(MatDialog);
  private scriptsRepository = inject(ScriptRepositoryService);
  private patientService = inject(PatientService);
  private patientEventService = inject(PatientEventsService);
  private coreService = inject(CoreService);
  private pushService = inject(WebSocketService);

  // Signals for shared widget
  isProcessing = signal(false);
  private medicationSignal = signal<Medication[]>([]);

  // Permission computed signals
  canAdd = computed(() => this.coreService.hasPermission('Clinical.Medication.Write'));
  canDelete = computed(() => this.coreService.hasPermission('Clinical.Medication.Write'));
  canSaveScript = computed(() => this.coreService.hasPermission('Clinical.Scripts.Write'));

  // Map medication to display items for shared widget
  medicationItems = computed<MedicationDisplayItem[]>(() =>
    this.medicationSignal().map(item => ({
      id: item.id,
      description: item.description,
      dosage: item.dosage,
      diagnosisCode: item.diagnosisCode,
      comment: item.comment,
      lastModifiedDate: item.lastModifiedDate,
      lastModifiedUser: item.lastModifiedUser,
      typeId: item.typeId,
      typeUnassigned: item.type?.unassigned,
      textColour: item.textColour
    }))
  );

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasMedication(): boolean {
    return this.medication.length > 0;
  }

  get patientId(): number {
    return this.patientService.currentPatient.id;
  }

  get patient(): Patient {
    return this.patientService.currentPatient;
  }

  get confirmationMessage(): string {
    return 'Are you sure you want to save a chronic script with this medication for ' + this.patient.firstName + ' ' + this.patient.surname + '?';
  }

  searchMedication$ = (query: string): Observable<Medication[]> => {
    return this.medicationRepository.query(query)
      .pipe(map(response => response));
  }

  ngOnInit(): void {
    this.channelName = this.patientService.currentPatientEventsChannelName;
    this.settings = this.coreService.settings();
    this.subscriptions.push(this.patientEventService.currentPatient$.subscribe(() => {
      this.loadPatientMedication();
    }));
    this.subscriptions.push(this.patientService.patientMedication$.subscribe(medication => {
      this.medication = medication.filter(item => item.scriptType === this.scriptType);
      this.medicationSignal.set(this.medication);
    }));
    this.subscriptions.push(this.patientEventService.events$.pipe(filter(e => e.type === 'medication_item_deleted')).subscribe(() => this.loadPatientMedication()));
    this.pushService.subscribe(this.channelName, this.eventName, _ => {
      this.loadPatientMedication();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.pushService.unsubscribe(this.channelName, this.eventName);
  }

  onAddNewItem(searchTerm: string) {
    const dialogRef = this.matDialog.open(MedicationItemAddCardComponent, {
      panelClass: 'mat-confirmation-dialog-responsive',
      width: '700px',
      data: { searchTerm }
    });

    this.subscriptions.push(dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onItemSelected(result);  // Direct add with full item (has ID)
      }
    }));
  }

  onItemSelected(medication: Medication) {
    medication.createdUser = this.coreService.currentUser;
    medication.lastModifiedUser = this.coreService.currentUser;
    medication.createdDate = new Date();
    medication.lastModifiedDate = new Date();
    this.medication.push(medication);
    this.medicationSignal.set([...this.medication]); // Update signal for visual refresh
    this.addMedicationToPatient(medication);
    // Note: cancelSearch is now handled internally by the shared widget
  }

  hasDuplicates(item: Medication): boolean {
    return this.medication.some(m => {
      if (m.description.length > item.description.length) {
        return m.description.indexOf(item.description) > -1 && m.id !== item.id;
      } else if (m.description.length < item.description.length) {
        return item.description.indexOf(m.description) > -1 && m.id !== item.id;
      } else {
        return m.description === item.description && m.id !== item.id;
      }
    });
  }

  hasDuplicateTypes(item: Medication): boolean {
    return this.medication.some(m => {
      return !item.type.unassigned && item.typeId === m.typeId && item.id !== m.id;
    });
  }

  saveScript() {
    this.scriptsRepository.saveScript(this.patientId, this.medication, this.scriptType).subscribe({
      next: (saved) => {
        this.patientEventService.emitEvent(new PatientEvent('scripts_updated'));
      },
      error: (error) => {
        this.coreService.showErrorToast('Could not save script');
        console.error(error);
      }
    });
  }

  onItemRemoved(event: MedicationRemovedEvent) {
    const medication = this.medication.find(m => m.id === event.item.id);
    if (medication) {
      this.isProcessing.set(true);
      this.medicationRepository.removeMedicationFromPatient(this.patientId, medication.id).subscribe({
        next: () => {
          this.medication = this.medication.filter(m => m.id !== medication.id);
          this.medicationSignal.set(this.medication);
          this.patientEventService.emitEvent(new PatientEvent('medication_item_deleted'));
          this.isProcessing.set(false);
        },
        error: (error) => {
          this.coreService.showErrorToast('Could not remove medication item');
          this.isProcessing.set(false);
        }
      });
    }
  }

  private loadPatientMedication() {
    if (!this.patientService.hasPatientDetails) {
      return;
    }
    this.patientService.loadPatientMedication();
  }

  private addMedicationToPatient(medication: Medication) {
    this.error = '';
    medication.scriptType = this.scriptType;
    this.medicationRepository.addMedicationToPatient(this.patientId, medication).subscribe({
      next: (newId) => {
        this.patientEventService.emitEvent(new PatientEvent('medication_item_added'));
        medication.id = newId;
      },
      error: (error) => {
        this.error = 'Could not add new medication item to this patient';
        this.loadPatientMedication();
      }
    });
  }
}
